// src/mediaProbe.ts
// Reads an asset's display metadata (dimensions, transparency, duration)
// and makes its thumbnail in the browser before upload, so the server
// needs no image/video tooling. These values are catalog hints only — the
// server detects the real file type itself (api/src/fileSniff.ts), and the
// render worker reads the actual file when it renders.

import { THUMB_MAX_DIMENSION, type AssetKind } from '@ring/shared/assets';

export interface ProbeResult {
  kind:      AssetKind | null;
  width?:    number;
  height?:   number;
  hasAlpha?: boolean;
  durationS?: number;
  thumb?:    Blob;
}

const PROBE_TIMEOUT_MS = 15_000;

/** Best guess from the browser's MIME type; the server has the final say. */
export function guessKind(file: File): AssetKind | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'music';
  if (file.type.startsWith('video/')) return 'video';
  return null;
}

function withTimeout<T>(promise: Promise<T>, what: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`Timed out reading ${what}.`)), PROBE_TIMEOUT_MS)),
  ]);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Could not make a thumbnail.'))), type, quality);
  });
}

/** Draws `source` scaled to fit THUMB_MAX_DIMENSION. WebP keeps transparency; JPEG is smaller for opaque images. */
async function makeThumb(source: CanvasImageSource, width: number, height: number, keepAlpha: boolean): Promise<Blob> {
  const scale = Math.min(1, THUMB_MAX_DIMENSION / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  canvas.getContext('2d')!.drawImage(source, 0, 0, canvas.width, canvas.height);
  return keepAlpha ? canvasToBlob(canvas, 'image/webp', 0.85) : canvasToBlob(canvas, 'image/jpeg', 0.82);
}

/** Samples the image at low resolution and reports whether any pixel is meaningfully transparent. */
function detectAlpha(img: HTMLImageElement): boolean {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 250) return true;
  }
  return false;
}

async function probeImage(file: File, url: string): Promise<ProbeResult> {
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  await withTimeout(img.decode(), 'the image');
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  // JPEG and BMP have no alpha channel worth checking.
  const hasAlpha = file.type === 'image/png' || file.type === 'image/webp' ? detectAlpha(img) : false;
  return { kind: 'image', width, height, hasAlpha, thumb: await makeThumb(img, width, height, hasAlpha) };
}

async function probeVideo(url: string): Promise<ProbeResult> {
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'metadata';
  video.src = url;
  await withTimeout(new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('This browser can’t read that video.'));
  }), 'the video');
  const durationS = Number.isFinite(video.duration) ? video.duration : undefined;
  const width = video.videoWidth;
  const height = video.videoHeight;

  // Grab a frame a little way in — frame 0 is often black.
  let thumb: Blob | undefined;
  try {
    video.currentTime = Math.min(1, (durationS ?? 2) / 2);
    await withTimeout(new Promise<void>(resolve => { video.onseeked = () => resolve(); }), 'a video frame');
    thumb = await makeThumb(video, width, height, false);
  } catch {
    thumb = undefined; // A missing thumbnail isn't worth failing the upload over.
  }
  return { kind: 'video', width, height, durationS, thumb };
}

async function probeAudio(url: string): Promise<ProbeResult> {
  const audio = document.createElement('audio');
  audio.preload = 'metadata';
  audio.src = url;
  await withTimeout(new Promise<void>((resolve, reject) => {
    audio.onloadedmetadata = () => resolve();
    audio.onerror = () => reject(new Error('This browser can’t read that audio file.'));
  }), 'the audio');
  return { kind: 'music', durationS: Number.isFinite(audio.duration) ? audio.duration : undefined };
}

/**
 * Never throws for an unreadable file — returns what it could get, so the
 * upload still goes ahead and the server decides whether the file is valid.
 */
export async function probeFile(file: File): Promise<ProbeResult> {
  const kind = guessKind(file);
  if (!kind) return { kind: null };
  const url = URL.createObjectURL(file);
  try {
    if (kind === 'image') return await probeImage(file, url);
    if (kind === 'video') return await probeVideo(url);
    return await probeAudio(url);
  } catch {
    return { kind };
  } finally {
    URL.revokeObjectURL(url);
  }
}
